import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useUser from '../hooks/useUser';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';
import BotSummaryList from '../components/BotSummaryList';
import Loading from '../components/Loading';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import AiPromptComposer from '../components/AiPromptComposer/AiPromptComposer';
import { setAgentHandoff } from '../utils/aiAgentHandoff';
import { savePromptToSession } from '../utils/aiPromptStorage';
import { buildAuthOverlayRouteState } from '../utils/authOverlayNavigation';
import { navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';
import "./Home.css";
import "./HomeLovable.css";

const LOGO_BASE = `${process.env.PUBLIC_URL || ''}/images/logos`;

const BROKER_LOGOS = [
  { name: 'Charles Schwab', file: 'schwab.png', url: 'https://www.schwab.com' },
  { name: 'Interactive Brokers', file: 'interactive-brokers.png', url: 'https://www.interactivebrokers.com' },
  { name: 'Alpaca', file: 'alpaca.png', url: 'https://alpaca.markets' },
  { name: 'Tradier', file: 'tradier.png', url: 'https://tradier.com' },
  { name: 'Tradeovate', file: 'tradeovate.png', url: 'https://www.tradeovate.com' },
  { name: 'Coinbase', file: 'coinbase.png', url: 'https://www.coinbase.com' },
  { name: 'Binance', file: 'binance.png', url: 'https://www.binance.com' },
  { name: 'Kraken', file: 'kraken.png', url: 'https://www.kraken.com' },
  { name: 'KuCoin', file: 'kucoin.png', url: 'https://www.kucoin.com' },
  { name: 'NinjaTrader', file: 'ninjatrader.png', url: 'https://ninjatrader.com' },
];

const IDEA_PROMPTS = [
  {
    title: 'AAPL Momentum Strategy',
    body: 'Trade Apple with a simple trend + momentum rule set and conservative risk limits.',
    prompt: 'Build a momentum strategy for AAPL. Use a trend filter (e.g., price above 200-day SMA) and enter on breakouts to a 20-day high with volume above its 20-day average. Use position sizing of 1–2% risk per trade, an ATR-based stop, and a trailing stop. Avoid trading during major earnings announcements. Backtest over the last 5 years.',
  },
  {
    title: 'SPY Options Spreads',
    body: 'Define a rules-based SPY credit spread playbook with clear entry/exit and risk caps.',
    prompt: 'Create a rules-based SPY options credit spread strategy. Sell weekly put credit spreads when SPY is above the 50-day SMA and RSI(14) is between 40–60. Target ~10–15 delta short leg, width $5–$10, max risk per trade 1% of account. Exit at 50% profit or 2x premium loss, and do not open new trades 2 days before CPI/FOMC. Backtest over the last 5–10 years.',
  },
  {
    title: 'Crypto Arbitrage Bot',
    body: 'A realistic “spread capture” bot with strict guardrails (fees, slippage, and limits).',
    prompt: 'Design a crypto spread-capture strategy (arbitrage-style) that is realistic: include exchange fees, slippage, and minimum profit thresholds. Monitor two venues for BTC/USDT; enter only when the price spread exceeds fees + slippage + a safety buffer. Use limit orders, max position size caps, and a kill-switch when volatility spikes. Include safety checks for partial fills and API errors. Backtest using historical spreads if available; otherwise simulate with conservative assumptions.',
  },
];

const STARTER_STRATEGY_IDEAS = [
  {
    title: 'TQQQ SMA 200 Strategy',
    body: 'Trade TQQQ using a clean 200-day moving average trend rule.',
    prompt:
      "Make a bot that trades the ticker symbol TQQQ. Buy TQQQ when the price is above the 200-day simple moving average (SMA 200). Sell TQQQ when the price is below the SMA 200. Use end-of-day data and place orders at the next market open. Add basic guardrails: max position size 100% of allocated capital, and a 1-day cooldown after a sell before buying again. Skip signals if today's volume is below its 20-day average.",
  },
  {
    title: 'SPY Options Spreads',
    body: 'Rules-based SPY credit spreads with clear entry/exit and risk caps.',
    prompt:
      'Create a rules-based SPY options credit spread strategy. Sell weekly put credit spreads when SPY is above the 50-day SMA and RSI(14) is between 40-60. Target ~10-15 delta short leg, width $5-$10, max risk per trade 1% of account. Exit at 50% profit or 2x premium loss, and do not open new trades 2 days before CPI/FOMC. Include rules for selecting expiration, handling assignment risk, and limiting total concurrent positions.',
  },
  {
    title: 'BTC Trend + Pullback',
    body: 'Trade BTC with a simple trend filter and controlled pullback entries.',
    prompt:
      'Build a crypto strategy for BTC/USD (or BTC/USDT). Use a trend filter: only trade long when price is above the 200-day moving average. For entries, wait for a pullback where RSI(14) dips below 40, then buy when RSI crosses back above 45. Use a stop loss at 2x ATR(14) and take profit at 3x ATR(14), plus a trailing stop once in profit. Limit to one open position and avoid trading during extreme volatility spikes (e.g., ATR(14) > 2x its 90-day average).',
  },
];

const EXPERIMENT_META = {
  experiment_name: 'homepage_ai_prompt_test',
  experiment_variant: 'ai_prompt',
};

const FLOATING_SHAPES = [
  'lovable-floating--bot',
  'lovable-floating--brain',
  'lovable-floating--spark',
  'lovable-floating--chart',
];

/**
 * @param {{
 *   suppressSeo?: boolean,
 *   experimentMeta?: Record<string, string | boolean | null | undefined> | null,
 * }} props
 */
export default function HomeLovable({ suppressSeo = false, experimentMeta = null }) {
  const { isAuthenticated, isLoading: authLoading } = useUser();
  const {
    isPayingCustomer,
    isLoading: paymentLoading,
    requiresSessionRefresh,
    error: paymentStatusError
  } = usePaymentStatus();
  const [aiPrompt, setAiPrompt] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const textareaRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get destination variant override from URL (?dv=checkout or ?dv=ai_agent)
  // This must be preserved when navigating to sales page for A/B test to work
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const destinationOverride = searchParams.get('dv');
  const activeExperimentMeta = useMemo(
    () => ({ ...EXPERIMENT_META, ...(experimentMeta || {}) }),
    [experimentMeta],
  );

  const isBusy = authLoading;
  const salesUrlBase = '/sales?showLogin=1&source=home-ai-input';

  const finalLogos = useMemo(
    () =>
      BROKER_LOGOS.map((logo) => ({
        ...logo,
        src: `${LOGO_BASE}/${logo.file}`,
      })),
    [],
  );

  const recordCta = (ctaLocation, overrides = {}) => {
    pushEvent({
      event: ANALYTICS_EVENTS.GET_STARTED_CLICK,
      button_text: overrides.button_text || 'Launch AI Builder',
      source: '/',
      destination: overrides.destination || salesUrlBase,
      user_type: isAuthenticated ? 'authenticated' : 'anonymous',
      is_paying_customer: isPayingCustomer,
      cta_type: ctaLocation,
      cta_location: ctaLocation,
      ...activeExperimentMeta,
      ...overrides,
    });
  };

  /**
   * Universal navigation handler for all CTAs on the homepage.
   * Routes users based on their authentication and payment status:
   * - Not logged in → Sales page (to collect contact info)
   * - Logged in + NOT paying → Checkout page (starter plan)
   * - Logged in + paying → AI Agent (with prompt if provided)
   */
  const handleNavigate = (ctaLocation = 'ai_prompt', userPrompt = '') => {
    const trimmedPrompt = userPrompt.trim();

    console.log('[HomeLovable] handleNavigate called:', {
      ctaLocation,
      userPrompt,
      trimmedPrompt,
      isAuthenticated,
      isPayingCustomer,
      paymentLoading,
    });

    // Save prompt to session for checkout flow (survives payment process)
    if (trimmedPrompt) {
      savePromptToSession(trimmedPrompt);
    }

    // 1. NOT LOGGED IN → Sales page with successUrl to preserve intended destination
    if (!isAuthenticated) {
      console.log('[HomeLovable] User NOT authenticated, redirecting to sales');

      // Store the handoff payload so AiAgent can pick it up after login
      // This survives the login flow (sessionStorage persists)
      // SECURITY: Include destinationVariant so checkout users can't bypass payment
      if (trimmedPrompt) {
        console.log('[HomeLovable] Setting agent handoff for post-login pickup:', trimmedPrompt.substring(0, 50));
        const handoffPayload = { prompt: trimmedPrompt, source: 'homepage' };
        // If there's a dv override, include it in the handoff
        if (destinationOverride) {
          handoffPayload.destinationVariant = destinationOverride;
        }
        setAgentHandoff(handoffPayload);
      }

      // Build sales URL preserving the dv param for A/B test destination
      // CRITICAL: The dv param must be preserved so checkout users go to checkout, not AI Agent
      let salesUrl = `/sales?showLogin=1&source=homepage`;
      if (destinationOverride) {
        salesUrl += `&dv=${encodeURIComponent(destinationOverride)}`;
      }
      // successUrl determines where user goes after auth - let the sales page handle routing
      // based on the A/B test (which is preserved via dv param)
      salesUrl += `&successUrl=${encodeURIComponent('/ai-agent')}`;

      recordCta(ctaLocation, {
        destination: salesUrl,
        prompt_preview: trimmedPrompt.substring(0, 140),
      });
      navigate(salesUrl, {
        state: buildAuthOverlayRouteState(location),
      });
      return;
    }

    // 2. LOGGED IN + PAYING → AI Agent directly
    if (isPayingCustomer) {
      console.log('[HomeLovable] User is PAYING, setting handoff and navigating to /ai-agent');
      if (trimmedPrompt) {
        console.log('[HomeLovable] Setting agent handoff with prompt:', trimmedPrompt.substring(0, 50));
        setAgentHandoff({ prompt: trimmedPrompt, source: 'homepage' });
      } else {
        console.log('[HomeLovable] WARNING: No prompt to handoff!');
      }
      recordCta(ctaLocation, {
        destination: '/ai-agent',
        prompt_preview: trimmedPrompt.substring(0, 140),
      });
      navigate('/ai-agent');
      return;
    }

    if (paymentLoading) {
      console.log('[HomeLovable] Payment status still loading, routing to protected AI Agent handoff');
      if (trimmedPrompt) {
        setAgentHandoff({ prompt: trimmedPrompt, source: 'homepage' });
      }
      recordCta(ctaLocation, {
        destination: '/ai-agent',
        prompt_preview: trimmedPrompt.substring(0, 140),
        auth_state: 'payment_status_loading',
      });
      navigate('/ai-agent');
      return;
    }

    if (requiresSessionRefresh || paymentStatusError) {
      const authState = requiresSessionRefresh ? 'requires_session_refresh' : 'payment_status_unavailable';
      console.log('[HomeLovable] Payment status unavailable, routing to protected AI Agent state', {
        requiresSessionRefresh,
        paymentStatusError,
      });
      if (trimmedPrompt) {
        setAgentHandoff({ prompt: trimmedPrompt, source: 'homepage' });
      }
      recordCta(ctaLocation, {
        destination: '/ai-agent',
        prompt_preview: trimmedPrompt.substring(0, 140),
        auth_state: authState,
      });
      navigate('/ai-agent');
      return;
    }

    // 3. LOGGED IN + NOT PAYING → Checkout page
    console.log('[HomeLovable] User is authenticated but NOT paying, redirecting to checkout');
    const checkoutUrl = '/checkout/starter-plan?redirect=starter_plan';
    recordCta(ctaLocation, {
      destination: checkoutUrl,
      prompt_preview: trimmedPrompt.substring(0, 140),
    });
    navigateToCheckoutOverlay(navigate, checkoutUrl, location);
  };

  const handleDemoModalOpen = () => {
    recordCta('demo_section', { destination: 'video_modal' });
    setVideoModalOpen(true);
  };

  const handleDemoModalClose = () => {
    setVideoModalOpen(false);
  };

  useEffect(() => {
    if (isVideoModalOpen) {
      document.body.classList.add('lovable-modal-open');
    } else {
      document.body.classList.remove('lovable-modal-open');
    }

    return () => {
      document.body.classList.remove('lovable-modal-open');
    };
  }, [isVideoModalOpen]);

  const handlePromptSubmit = () => {
    console.log('[HomeLovable] handlePromptSubmit called, aiPrompt state:', aiPrompt);
    handleNavigate('ai_prompt', aiPrompt || '');
  };

  const handleAttachmentsSelected = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachments(files);
  };

  const handleRemoveAttachment = (attachmentId) => {
    setAttachments((prev) => prev.filter((file) => (file.id || file.name) !== attachmentId));
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const handlePromptKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handlePromptSubmit();
    }
  };

  const handleHeroPrimaryCta = () => {
    // Uses same routing logic as all other CTAs on the page
    handleNavigate('hero_primary', aiPrompt || '');
  };

  if (isBusy) {
    return <Loading />;
  }

  return (
    <>
    <div className="home home-lovable">
      {!suppressSeo && (
        <Helmet>
          <title>BotSpot - Describe your trading idea, we build the bot</title>
          <meta
            name="description"
            content="A future-grade BotSpot experience. Sketch your idea, stream the fabrication pipeline, and deploy to your brokers without surrendering custody."
          />
          <link rel="canonical" href="https://botspot.trade/" />
        </Helmet>
      )}

      <section className="lovable-hero">
        <div className="lovable-hero-background" />
        <div className="lovable-hero-effects" aria-hidden="true">
          <div className="lovable-floating-cluster">
            {FLOATING_SHAPES.map((variant) => (
              <span key={variant} className={`lovable-floating ${variant}`} />
            ))}
          </div>
          <div className="lovable-particles">
            {Array.from({ length: 8 }).map((_, index) => (
              <span key={`particle-${index}`} className={`lovable-particle lovable-particle--${index + 1}`} />
            ))}
          </div>
          <div className="lovable-haze lovable-haze--purple" />
          <div className="lovable-haze lovable-haze--cyan" />
          <div className="lovable-haze lovable-haze--amber" />
        </div>

        <div className="lovable-hero-content">
          <h1>
            Build your trading bot.
            <span>Describe your strategy, we'll build it in minutes.</span>
          </h1>
          <p className="lovable-lede">Turn trading ideas into live bots by chatting with AI. No coding required.</p>

          <div id="lovable-chatbox" className="aibot-input-area-container" data-testid="input-area">
            <AiPromptComposer
              value={aiPrompt}
              onChange={setAiPrompt}
              onKeyDown={handlePromptKeyDown}
              onSubmit={handlePromptSubmit}
              textareaRef={textareaRef}
              canSubmit={aiPrompt.trim().length > 0}
            sendButtonAriaLabel="Generate this idea"
            sendButtonTestId="lovable-generate-button"
            attachmentsConfig={{
              enabled: true,
              inputRef: attachmentInputRef,
              onAttachmentsSelected: handleAttachmentsSelected,
              attachments,
              onRemoveAttachment: handleRemoveAttachment,
              attachmentButtonLabel: 'Attach files',
              attachmentButtonAriaLabel: 'Attach files',
              attachmentButtonDisabled: false,
            }}
            voiceEnabled
            onVoiceError={(err) => console.error('[HomeLovable] Voice error:', err)}
          />
        </div>
        </div>
      </section>

      <section className="lovable-broker-carousel" aria-label="Broker integrations carousel">
        <div className="lovable-broker-carousel-track">
          {[...finalLogos, ...finalLogos].map((logo, idx) => (
            <div key={`${logo.name}-${idx}`} className="lovable-broker-card">
              <a
                href={logo.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${logo.name} website`}
              >
                <img src={logo.src} alt={`${logo.name} logo`} loading="lazy" />
              </a>
            </div>
          ))}
        </div>
      </section>

      <section className="lovable-broker-strip">
        <div className="broker-text">
          <span className="broker-eyebrow">Broker integrations</span>
          <h2>Connect the accounts you already have.</h2>
          <p>
            Your capital stays with your broker. BotSpot connects securely to execute trades without ever touching your funds.
          </p>
          <button
            type="button"
            className="lovable-spectrum-cta-button"
            onClick={() => handleNavigate('broker_cta', aiPrompt)}
          >
            Try it now
          </button>
        </div>
      </section>

      <section className="lovable-inline-demo" aria-label="Bot builder quick demo">
        <div className="lovable-inline-demo-header">
          <span className="broker-eyebrow">See how it works</span>
        </div>
        <div className="lovable-inline-demo-shell">
          <div className="lovable-inline-demo-card">
            <div className="lovable-inline-demo-frame">
              <div className="lovable-inline-demo-video">
                <iframe
                  title="Bot builder walkthrough"
                  src="https://www.youtube.com/embed/TE75ytXCAPA?autoplay=1&mute=1&controls=1&loop=1&playlist=TE75ytXCAPA"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Visual - Before/After Story */}
      <section className="lovable-transformation" aria-label="Your transformation journey">
        <div className="lovable-transformation-container">
          <picture>
            <source
              srcSet={`${process.env.PUBLIC_URL || ''}/images/bootcamp-transformation-1000.webp`}
              type="image/webp"
            />
            <img
              src={`${process.env.PUBLIC_URL || ''}/images/bootcamp-transformation-1000.jpg`}
              alt="Transform from manual trading stress to AI-powered automated wealth"
              className="lovable-transformation-image"
              loading="lazy"
              decoding="async"
              width="1000"
              height="545"
            />
          </picture>
        </div>
      </section>

      <section className="lovable-spectrum">
        <h3>From idea to live bot in minutes.</h3>
        <div className="spectrum-grid">
          <div className="spectrum-card">
            <span className="spectrum-step">Step 1</span>
            <h4>Describe</h4>
            <p>Tell us your trading strategy in plain English - stocks, options, crypto, or futures. The AI figures out the logic.</p>
          </div>
          <div className="spectrum-card">
            <span className="spectrum-step">Step 2</span>
            <h4>Test</h4>
            <p>Run backtests on historical data to see how your strategy would have performed.</p>
          </div>
          <div className="spectrum-card">
            <span className="spectrum-step">Step 3</span>
            <h4>Deploy</h4>
            <p>Connect your broker and let the bot start trading when you're ready.</p>
          </div>
          <div className="spectrum-card">
            <span className="spectrum-step">Step 4</span>
            <h4>Monitor</h4>
            <p>Track performance in real-time. Pause or adjust anytime.</p>
          </div>
        </div>
      </section>

      <section className="lovable-spectrum-cta-row">
        <div className="lovable-spectrum-cta">
          <button
            type="button"
            className="lovable-spectrum-cta-button lovable-cta-button--blue"
            onClick={() => handleNavigate('spectrum_cta', aiPrompt)}
          >
            Start describing your bot
          </button>
        </div>
      </section>

      {/* ============================================================
          Agents callout: alternate top-of-funnel for users who already
          live in ChatGPT, Claude, or Cursor. Drives traffic to /agents
          where the OAuth/MCP install flow is documented. Tracked via
          analytics so we can attribute installs to the homepage button.
          ============================================================ */}
      <section className="lovable-agents-callout" aria-label="Use BotSpot in your AI assistant">
        <div className="lovable-agents-callout-inner">
          <span className="lovable-agents-callout-eyebrow">For ChatGPT, Claude, and Cursor users</span>
          <h3 className="lovable-agents-callout-title">Already use an AI assistant? Use BotSpot from inside it.</h3>
          <p className="lovable-agents-callout-body">
            Connect BotSpot to ChatGPT, Claude, Cursor, or any MCP-compatible client in one click. Build trading strategies, run backtests, and deploy live bots without leaving the chat you already use every day.
          </p>
          <Link
            to="/agents"
            className="lovable-spectrum-cta-button lovable-agents-callout-cta"
            onClick={() => {
              try {
                pushEvent({
                  event: ANALYTICS_EVENTS.HOMEPAGE_AGENTS_CALLOUT_CLICK,
                  source: 'home_lovable',
                  destination: '/agents',
                });
              } catch (err) {
                // Analytics is fire-and-forget; never break navigation.
              }
            }}
          >
            See how to connect
          </Link>
        </div>
      </section>

      <section className="lovable-bot-list">
        <div className="home-section-header">
          <h2 className="home-section-title">Strategy Library</h2>
          <p className="home-section-subtitle">Browse marketplace strategies with transparent live history and backtests. Filter by keyword to explore more.</p>
        </div>

        <div className="home-search-section lovable-search">
          <div className="home-search-container">
            <input
              type="text"
              placeholder="Search the strategy library"
              className="home-search-input"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
        </div>

        <BotSummaryList
          isHomePage
          homeSearchQuery={searchQuery}
        />
      </section>

      <section className="lovable-ideas">
        <header>
          <h3>Not sure where to start?</h3>
          <p>Try one of these ideas or describe your own strategy above.</p>
        </header>
        <div className="lovable-ideas-grid">
          {STARTER_STRATEGY_IDEAS.map((prompt) => (
            <article key={prompt.title} className="lovable-idea-card">
              <h4>{prompt.title}</h4>
              <p>{prompt.body}</p>
              <button
                type="button"
                className="lovable-primary-cta lovable-primary-cta--glow lovable-primary-cta--compact"
                onClick={() => {
                  setAiPrompt((prompt.prompt || prompt.title).replace(/"/g, ''));
                  textareaRef.current?.focus();
                }}
              >
                <span>Load this idea</span>
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="lovable-story">
        <div className="lovable-story-text">
          <p className="lovable-pill">
            Why <span className="lovable-pill__brand">BotSpot</span> exists
          </p>
          <h2>Trading ideas shouldn't take months to build.</h2>
          <p>
            Most traders have great ideas but hit a wall: hiring developers is expensive and slow.
            BotSpot turns that months-long process into minutes by letting you describe what you want in plain English.
          </p>
          <ul>
            <li>Describe your strategy like you're explaining it to a friend.</li>
            <li>Review and test everything before it trades real money.</li>
            <li>Deploy to your broker when you're ready.</li>
          </ul>
        </div>
        <div className="lovable-story-media">
          <img src={`${process.env.PUBLIC_URL || ''}/images/rob.jpg`} alt="Rob explaining how BotSpot works" loading="lazy" />
          <div className="lovable-story-caption">
            Rob G | Founder, BotSpot. Turn your trading ideas into reality.
          </div>
        </div>
      </section>

      {/* Enterprise Section: signals "we also serve institutions" */}
      <section className="lovable-enterprise">
        <div className="lovable-enterprise-inner">
          <h2 className="lovable-enterprise-title">Built for Professional Trading Teams</h2>
          <p className="lovable-enterprise-subtitle">
            From family offices to prop firms, BotSpot powers institutional trading operations.
          </p>
          <div className="lovable-enterprise-cards">
            <div className="lovable-enterprise-card">
              <i className="fas fa-exchange-alt lovable-enterprise-card-icon" />
              <h3>Multi-Broker Execution</h3>
              <p>Deploy across 10+ brokerages. One platform, every venue.</p>
            </div>
            <div className="lovable-enterprise-card">
              <i className="fas fa-shield-alt lovable-enterprise-card-icon" />
              <h3>Compliance and Audit Trails</h3>
              <p>Full history of every strategy change, backtest, and trade.</p>
            </div>
            <div className="lovable-enterprise-card">
              <i className="fas fa-cogs lovable-enterprise-card-icon" />
              <h3>Custom Integrations</h3>
              <p>Tailored solutions for your firm's specific workflow and tools.</p>
            </div>
          </div>
          <Link to="/enterprise" className="lovable-enterprise-cta">
            Talk to Our Team
          </Link>
        </div>
      </section>

      <section className="lovable-final-cta">
        <h3>Ready to describe your bot?</h3>
        <p>Hit Enter in the prompt above or tap the button below. The AI command center will be waiting for you.</p>
        <button
          type="button"
          className="lovable-primary-cta lovable-primary-cta--glow"
          onClick={() => handleNavigate('final_cta', aiPrompt)}
        >
          <span>Start my free build session</span>
        </button>
      </section>

    </div>

      {isVideoModalOpen && <div className="lovable-overlay" aria-hidden="true" />}

      {isVideoModalOpen && (
        <div className="lovable-video-modal" role="dialog" aria-modal="true" aria-label="Bot builder walkthrough video">
          <button
            type="button"
            className="lovable-video-modal__backdrop"
            aria-label="Close video overlay"
            onClick={handleDemoModalClose}
          />
          <div className="lovable-video-modal__body">
            <div className="lovable-video-modal__content">
              <button
                type="button"
                className="lovable-video-modal__close"
                aria-label="Close video"
                onClick={handleDemoModalClose}
              >
                <span />
              </button>
              <div className="lovable-video-modal__video">
                <div className="lovable-video-modal__shell">
                  <div className="lovable-video-modal__badge">60 SECOND WALKTHROUGH</div>
                  <h3>See how the bot builder works.</h3>
                  <div className="lovable-video-modal__frame">
                    <iframe
                      className="lovable-video-modal__media"
                      title="Bot builder walkthrough video"
                      src="https://www.youtube.com/embed/TE75ytXCAPA?autoplay=0&controls=1"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
